import { HttpClient } from '@adapters/HttpClient';
import { appendPackagingDimensionsToFormData } from '../../shared/utils/packagingDimensions';
import {
  BoxGroupItem,
  BoxGroupsApiResponse,
  CreateBoxGroupRequest,
  CreatePackagingRequest,
  PackagingApiResponse,
  PackagingFilters,
  PackagingItem,
  PackagingPage,
  BoxGroupCategory,
} from '../models/Packaging';

export class PackagingApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // Listado paginado con filtros
  async getPackagingPaginated(filters: PackagingFilters = {}): Promise<PackagingPage> {
    const { searchTerm = '', categories, teamId, active, page = 1, limit = 20 } = filters;

    const params: any = {
      ...(searchTerm && searchTerm.trim().length > 0 && { search: searchTerm.trim() }),
      ...(categories?.length && { 'categories[]': categories }),
      ...(teamId && { team_id: teamId }),
      ...(active !== undefined && { active }),
      page,
      per_page: limit,
    };

    const showGlobalLoading = page === 1;

    const response = await this.httpClient.get<PackagingApiResponse>('/packaging', params, {}, showGlobalLoading);

    return {
      items: response.data,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  // Obtener listado simple (sin paginación)
  async getPackaging(filters: PackagingFilters = {}): Promise<PackagingItem[]> {
    const response = await this.getPackagingPaginated(filters);
    return response.items;
  }

  // Obtener grupos de cajas para selector en creación/edición
  async getBoxGroups(teamId?: number, boxCategory?: BoxGroupCategory): Promise<BoxGroupItem[]> {
    const params: Record<string, string | number | string[]> = {
      page: 1,
      per_page: 20,
      'sorting[column]': 'id',
      'sorting[direction]': 'desc',
      'advanced_filters[logic]': 'AND',
      ...(boxCategory ? { box_category: boxCategory } : {}),
    };

    if (teamId) {
      params['advanced_filters[filters][0][column]'] = 'team_id';
      params['advanced_filters[filters][0][operator]'] = 'in';
      params['advanced_filters[filters][0][value][]'] = [String(teamId)];
    }

    const response = await this.httpClient.get<BoxGroupsApiResponse>('/box-groups', params, {}, false);

    return response.data;
  }

  // Crear grupo de cajas asociado a un team
  async createBoxGroup(data: CreateBoxGroupRequest): Promise<BoxGroupItem> {
    return this.httpClient.post<BoxGroupItem>('/box-groups', data);
  }

  // Crear nuevo material de packaging
  async createPackaging(data: CreatePackagingRequest): Promise<PackagingItem> {
    // Si hay imagen, usar FormData (multipart)
    if (data.image_hash) {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('sku', data.sku);
      formData.append('category', data.category);
      if (data.team_id !== undefined) formData.append('team_id', String(data.team_id));
      if (data.pvp !== undefined) formData.append('pvp', String(data.pvp));
      if (data.packaging_box_group_id !== undefined)
        formData.append('packaging_box_group_id', String(data.packaging_box_group_id));
      appendPackagingDimensionsToFormData(formData, data);
      formData.append('image_hash', data.image_hash);

      const response = await this.httpClient.post<PackagingItem>('/packaging', formData);
      return response;
    }

    // Sin imagen, enviar JSON
    const response = await this.httpClient.post<PackagingItem>('/packaging', data);
    return response;
  }

  // Editar material de packaging
  async updatePackaging(id: number, data: CreatePackagingRequest): Promise<PackagingItem> {
    // Con imagen, backend requiere method spoofing:
    // POST multipart + _method=PUT
    if (data.image_hash) {
      const formData = new FormData();
      formData.append('_method', 'PUT');
      formData.append('name', data.name);
      formData.append('sku', data.sku);
      formData.append('category', data.category);
      // Backend espera siempre la clave team_id en update multipart.
      formData.append('team_id', data.team_id !== null && data.team_id !== undefined ? String(data.team_id) : '');
      if (data.pvp !== undefined) formData.append('pvp', String(data.pvp));
      if (data.packaging_box_group_id !== undefined)
        formData.append('packaging_box_group_id', String(data.packaging_box_group_id));
      appendPackagingDimensionsToFormData(formData, data);
      formData.append('image_hash', data.image_hash);

      return this.httpClient.post<PackagingItem>(`/packaging/${id}`, formData);
    }

    return this.httpClient.put<PackagingItem>(`/packaging/${id}`, {
      ...data,
      team_id: data.team_id ?? null,
    });
  }

  // Eliminar material (backend aún no implementa DELETE, pero lo dejamos preparado)
  async deletePackaging(id: number): Promise<void> {
    await this.httpClient.delete(`/packaging/${id}`);
  }
}
